import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { IFilterOptions } from '@legacy/apis/types/dropdownAPI';
import { getFetch } from '@legacy/utils/fetcher';
import { concatQueries } from '@legacy/utils/url';

interface IDropdown {
  label: string;
  value: string;
}

interface IDropdownFetch {
  contentUrl: string;
  key: string;
  pageIdentifier: string;
  take: number;
  skip: string;
  queryString?: string;
  searchText?: string;
}

const dropdownFetch = ({
  contentUrl,
  key,
  pageIdentifier,
  queryString,
  searchText,
  skip,
  take,
}: IDropdownFetch): Promise<IFilterOptions> =>
  getFetch({
    path: `/${contentUrl}?pageIdentifier=${pageIdentifier}${queryString ?? ''}${concatQueries(
      [
        { param: 'searchText', value: searchText },
        { param: 'skip', value: skip },
        { param: 'take', value: String(take) },
      ],
      true,
    )}`,
    key,
  });

interface IUseGetDropDownAPI {
  filterId: string;
  pageIdentifier: string;
  contentUrl?: string;
  queryString?: string;
  searchText?: string;
  take?: number;
}

export const flattenInfiniteFilterData = (
  data?: InfiniteData<IFilterOptions> | null,
) => {
  if (!data?.pages?.length) {
    return {
      dropdownList: [],
      latestPageChildrenLength: 0,
    };
  }

  const latestPage = data.pages[data.pages.length - 1];

  // flatten the array of arrays from the useInfiniteQuery hook
  const entities = (data?.pages?.flatMap((page) => page.entities) || []) ?? [];
  const properties =
    entities?.map((e) => e?.properties || { displayText: '', value: '' }) ?? [];

  return {
    dropdownList:
      properties.map<IDropdown>(({ displayText, value }) => ({
        label: displayText,
        value,
      })) ?? [],
    latestPageChildrenLength: latestPage?.entities?.length || 0,
  };
};

export const useGetDropdownAPI = ({
  contentUrl = '',
  filterId,
  pageIdentifier,
  queryString,
  searchText,
  take = 50,
}: IUseGetDropDownAPI) => {
  const key = `DropDownFilterInput${filterId}`;

  return useInfiniteQuery<IFilterOptions>({
    enabled: !!contentUrl,
    initialPageParam: 0,
    getNextPageParam: (_lastGroup, groups) => groups.length,
    queryFn: async ({ pageParam }) =>
      dropdownFetch({
        contentUrl,
        key,
        pageIdentifier,
        queryString,
        searchText,
        skip: String(typeof pageParam === 'number' ? pageParam * take : 0),
        take,
      }),
    queryKey: [key, { searchText, queryString, pageIdentifier, take }],
    staleTime: 5000,
  });
};
